"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowDown } from "lucide-react";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuContent, NavigationMenuItem, NavigationMenuList, NavigationMenuTrigger, NavigationMenuLink } from "@/components/ui/navigation-menu";
import { Link } from "@/components/common/Link";
import { MenuProvider } from "@/integrations/wordpress/WordPressMenuProvider";
const sections = [{
  name: "VPNS",
  id: "vpn-features"
}, {
  name: "Insights",
  id: "security-insights"
}, {
  name: "Cybersecurity CyberGenix",
  id: "cybersecurity-cybergenix"
}, {
  name: "SAAS Tools SAASGenix",
  id: "saas-tools-saasgenix"
}, {
  name: "AI Tools AIGenix",
  id: "ai-tools-aigenix"
}];
export default function DigitalFreedom() {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [mouse, setMouse] = useState({
    x: 50,
    y: 50
  });
  const [revealed, setRevealed] = useState(0);
  useEffect(() => {
    const node = sectionRef.current;
    if (!node) return;
    const handleMove = (e: MouseEvent) => {
      const rect = node.getBoundingClientRect();
      const x = (e.clientX - rect.left) / rect.width * 100;
      const y = (e.clientY - rect.top) / rect.height * 100;
      setMouse({
        x,
        y
      });
    };
    node.addEventListener("mousemove", handleMove);
    return () => node.removeEventListener("mousemove", handleMove);
  }, []);
  useEffect(() => {
    const lines = 4;
    const interval = setInterval(() => {
      setRevealed(prev => {
        if (prev >= lines) {
          clearInterval(interval);
          return prev;
        }
        return prev + 1;
      });
    }, 220);
    return () => clearInterval(interval);
  }, []);
  const headlineLines = [{
    text: "A NETWORK",
    accent: false
  }, {
    text: "AS SECURE AS",
    accent: false
  }, {
    text: "YOUR",
    accent: false
  }, {
    text: "SOVEREIGNTY.",
    accent: true
  }];
  const getRoute = (id: string) => {
    switch (id) {
      case 'cybersecurity-cybergenix':
        return '/CybersecurityCyberGenix';
      case 'saas-tools-saasgenix':
        return '/SaasToolsSaasGenix';
      case 'ai-tools-aigenix':
        return '/AiToolsAIGenix';
      default:
        return `#${id}`;
    }
  };
  return <section data-section-id="2759" id="digital-freedom" ref={sectionRef} className="dark bg-background text-foreground relative flex min-h-[90vh] w-full flex-col items-center justify-center overflow-hidden px-4 py-20 sm:px-8 md:px-16">
      <div className="absolute top-8 z-50">
        <MenuProvider menu_id="167">
          <NavigationMenu>
            <NavigationMenuList>
              <NavigationMenuItem>
                <NavigationMenuTrigger className="bg-transparent text-white hover:bg-white/10">
                  Navigate Sections
                </NavigationMenuTrigger>
                <NavigationMenuContent className="grid w-[200px] gap-1 p-2">
                  {sections.map(s => <NavigationMenuLink key={s.id} asChild>
                      <Link to={getRoute(s.id)} className="block px-4 py-2 text-sm">
                        {s.name}
                      </Link>
                    </NavigationMenuLink>)}
                </NavigationMenuContent>
              </NavigationMenuItem>
            </NavigationMenuList>
          </NavigationMenu>
        </MenuProvider>
      </div>

      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 transition-[background-position] duration-700 ease-out" style={{
      background: `radial-gradient(600px circle at ${mouse.x}% ${mouse.y}%, hsla(221, 83%, 53%, 0.15), transparent 60%)`
    }} />

      <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-0 opacity-[0.04]" style={{
      backgroundImage: "linear-gradient(var(--foreground) 1px, transparent 1px), linear-gradient(90deg, var(--foreground) 1px, transparent 1px)",
      backgroundSize: "80px 80px"
    }} />

      <div className="absolute left-4 top-1/2 z-10 hidden -translate-y-1/2 md:block">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground" style={{
        writingMode: "vertical-rl",
        transform: "rotate(180deg)"
      }}>
          PRIVATE · ENCRYPTED · UNRESTRICTED
        </div>
      </div>

      <div className="absolute right-4 top-1/2 z-10 hidden -translate-y-1/2 md:block">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-muted-foreground" style={{
        writingMode: "vertical-rl"
      }}>
          EST. 2024 · VPNGENIX
        </div>
      </div>

      <div className="relative z-20 mx-auto flex w-full max-w-6xl flex-col items-center text-center">
        <div className="mb-10 flex items-center gap-4 sm:mb-14">
          <span className="h-px w-8 bg-primary/40" />
          <span className="font-mono text-[10px] uppercase tracking-[0.4em] text-primary/70 sm:text-xs">
            The Digital Sovereignty Protocol / Secure Access
          </span>
          <span className="h-px w-8 bg-primary/40" />
        </div>

        <h1 className="font-default font-bold uppercase leading-[0.95] tracking-tight text-foreground">
          {headlineLines.map((line, i) => <span key={i} data-index={i} className="block overflow-hidden">
              <span className={`inline-block text-[3rem] transition-all duration-[900ms] ease-out sm:text-[5rem] md:text-[7rem] lg:text-[10rem] ${revealed > i ? "translate-y-0 opacity-100" : "translate-y-full opacity-0"} ${line.accent ? "text-primary" : ""}`} style={{
            transitionDelay: `${i * 80}ms`
          }}>
                {line.text}
              </span>
            </span>)}
        </h1>

        <p className={`mt-10 max-w-[600px] font-default text-lg leading-relaxed text-muted-foreground transition-all duration-1000 sm:text-xl ${revealed >= 4 ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`} style={{
        transitionDelay: "200ms"
      }}>
          The architecture of digital freedom begins with a single connection — who controls your data, truly? At VPNGENIX, our engineers translate complex encryption into a seamless shield that exists only for your protection.
        </p>

        <div className={`mt-12 flex flex-col sm:flex-row items-center justify-center gap-6 transition-all duration-1000 ${revealed >= 4 ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"}`} style={{
        transitionDelay: "400ms"
      }}>
          <Link to="#topratedvpncomparison">
            <Button size="lg" className="h-14 rounded-md bg-primary px-10 font-default text-sm font-bold uppercase tracking-[0.2em] text-white shadow-lg hover:bg-primary/90" style={{
            boxShadow: "8px 8px 0px 0px hsl(22, 100%, 50%)"
          }}>
              Start Your Protection
            </Button>
          </Link>
          <Link to="#">
            <Button size="lg" variant="outline" className="h-14 rounded-md border-primary/40 bg-background/50 px-8 font-default text-xs font-semibold uppercase tracking-[0.15em] text-foreground hover:bg-accent hover:text-accent-foreground">
              Discover how VPNGeniX is the foundation of a growing privacy‑focused ecosystem
            </Button>
          </Link>
        </div>
      </div>

      <div className="absolute bottom-6 left-1/2 z-20 -translate-x-1/2">
        <div className="flex animate-pulse items-center gap-2 font-mono text-[10px] uppercase tracking-[0.4em] text-muted-foreground sm:text-xs">
          <ArrowDown className="h-3 w-3" />
          <span>The Protocol Begins Here</span>
        </div>
      </div>

      <div className="absolute left-4 top-6 z-10 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground/40 sm:left-8 sm:top-8">
        N° 001
      </div>
      <div className="absolute right-4 top-6 z-10 font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground/40 sm:right-8 sm:top-8">
        VPNGENIX
      </div>
    </section>;
}